import * as sdk from '@canton-network/dapp-sdk';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useState } from 'react';
import { useDAppSdkClientResetter, useDAppSdkClientSetter } from '../contexts/DAppSdkContext';
import { getErrorMessage } from '../utils/errors';

type LoadingAction = 'connect' | 'disconnect' | 'none';

const notifyInfo = (message: string) => {
  console.log(`WalletGateway: ${message}`);
  enqueueSnackbar(message, { variant: 'info' });
};

const notifySuccess = (message: string) => {
  console.log(`WalletGateway: ${message}`);
  enqueueSnackbar(message, { variant: 'success' });
};

const notifyError = (message: string, error: unknown) => {
  const errorDetail = getErrorMessage(error);
  const errorText = `Error: ${message}, ${errorDetail}`;
  console.error(`WalletGateway: ${errorText}`, error);
  enqueueSnackbar(errorText, { variant: 'error' });
};

// More info on how to go about this: https://github.com/hyperledger-labs/splice-wallet-kernel/blob/e08fe7027a2223a40cb98b7cfc6210759094c997/examples/ping/src/App.tsx
const SpliceWalletControls = () => {
  const [status, setStatus] = useState<sdk.dappAPI.StatusEvent>();
  const [connectResult, setConnectResult] = useState<sdk.dappAPI.ConnectResult>();
  const [loadingAction, setLoadingAction] = useState<LoadingAction>('none');

  const setDAppSdkClient = useDAppSdkClientSetter();
  const resetDAppSdkClient = useDAppSdkClientResetter();

  const isWalletConnected =
    (status?.connection?.isConnected && status?.connection?.isNetworkConnected) ||
    (connectResult?.isConnected && connectResult?.isNetworkConnected);
  const isDisconnected = !isWalletConnected;
  const isBusy = loadingAction !== 'none';

  const onStatusChanged = (newStatus: sdk.dappAPI.StatusEvent) => {
    console.log('Wallet State Change.');
    setStatus(newStatus);
  };

  useEffect(() => {
    const provider = window.canton;
    if (isDisconnected && provider) {
      localStorage.clear();
      setLoadingAction('none');
      resetDAppSdkClient();
      notifyInfo('Disconnected from Wallet Gateway');
    }
  }, [isDisconnected, resetDAppSdkClient]);

  // Request accounts when connected
  useEffect(() => {
    const provider = window.canton;
    if (!provider || !isWalletConnected) {
      return undefined;
    }

    const handleBeforeUnload = () => {
      if (isWalletConnected) {
        // Fire-and-forget the disconnect - it may or may not complete during unload
        sdk
          .disconnect()
          // React state will be reset on refresh so no need to manually reset it
          .then(() => localStorage.clear())
          .catch((err) => {
            console.error('Error during disconnect on unload:', err);
          });
      }
    };

    console.log('Setting up event listeners for wallet events');
    // Event listener for connection status changes
    provider.on<sdk.dappAPI.StatusEvent>('statusChanged', onStatusChanged);
    provider.on<sdk.dappAPI.TxChangedEvent>('txChanged', (evt) => console.log('txchanged: ', evt));
    setDAppSdkClient(provider);
    // Handle page refresh/close - disconnect and clean up
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      console.log('Cleaning up event listeners for wallet events');
      provider.removeListener('statusChanged', onStatusChanged);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isWalletConnected, setDAppSdkClient]);

  const connectSdk = useCallback(async () => {
    console.log('Connecting to Wallet Gateway...');
    setLoadingAction('connect');

    try {
      const response = await sdk.connect();
      setConnectResult(response);
      const isConnected = response.isConnected && response.isNetworkConnected;
      if (isConnected) {
        notifySuccess('Connected to Wallet Gateway');
      } else {
        notifyInfo(
          'Connect successful, but not connected to wallet gateway. Please refresh and retry.',
        );
      }
    } catch (connectError) {
      notifyError('Cannot connect to wallet gateway', connectError);
    } finally {
      setLoadingAction('none');
    }
  }, []);

  const disconnectSdk = useCallback(async () => {
    setLoadingAction('disconnect');

    try {
      console.log('Disconnecting from Wallet Gateway...');
      await sdk.disconnect();
      // We rely on the statusChanged event to status state
    } catch (disconnectError) {
      notifyError('Cannot disconnect from wallet gateway', disconnectError);
    } finally {
      // We have an event listener that will clean up state on disconnect, onStatusChanged
      setLoadingAction('none');
      setStatus(undefined);
      setConnectResult(undefined);
    }
  }, []);

  const openWalletGateway = useCallback(async () => {
    try {
      await sdk.open();
    } catch (openError) {
      notifyError('Cannot open wallet gateway', openError);
    }
  }, []);

  // Disconnect on unmount (component removed from DOM). Though component should never be unmounted in normal app flow.
  useEffect(() => {
    return () => {
      if (isWalletConnected) {
        disconnectSdk().catch((err: unknown) => {
          console.error('Error disconnecting on unmount:', err);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canConnect = !isWalletConnected && !isBusy;
  const canDisconnect = isWalletConnected && !isBusy;
  const canOpenGateway = isWalletConnected && !isBusy;

  return (
    <div>
      {isWalletConnected ? (
        <div>
          <button
            type="button"
            disabled={!canDisconnect}
            onClick={() => {
              disconnectSdk().catch((err: unknown) => {
                console.error('Error disconnecting:', err);
              });
            }}
          >
            {loadingAction === 'disconnect' ? 'Disconnecting...' : 'Disconnect'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          disabled={!canConnect}
          onClick={() => {
            connectSdk().catch((err: unknown) => {
              console.error('Error connecting:', err);
            });
          }}
        >
          {loadingAction === 'connect' ? 'Connecting...' : 'Connect to Wallet Gateway'}
        </button>
      )}

      <button
        type="button"
        disabled={!canOpenGateway}
        onClick={() => {
          openWalletGateway().catch((err: unknown) => {
            console.error('Error opening wallet gateway:', err);
          });
        }}
      >
        Open Wallet Gateway
      </button>
    </div>
  );
};

export default SpliceWalletControls;
